import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import { getInvoiceList, getInvoiceById } from "../../services/invoiceService";
import { getAllDoctors } from "../../services/doctorService";
import { getAllDepartment } from "../../services/departmentService";
import { getAll as getAllAdmissions } from "../../services/PatientAdmissionService";
import { getAllPatients } from "../../services/patientService";
import { getAppointmentsGroupedByPatient, getAllAppointments } from "../../services/appointmentService";
import { fetchAssignmentsByAdmissionIds } from "../../services/patientPeriodSummaryService";
import ModeEditOutlineTwoToneIcon from "@mui/icons-material/ModeEditOutlineTwoTone";
import PrintIcon from "@mui/icons-material/Print";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  styled,
  useTheme,
  TextField,
  Stack,
  MenuItem,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import { toast } from "react-toastify";
import Tab from "@mui/material/Tab";
import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import PageShell from "../../components/PageShell";
import Refresh from "@mui/icons-material/Refresh";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import AddNewInvoice from "./AddNewInvoice";
import InvoiceAttachmentDownloadButton from "../../components/InvoiceAttachmentDownloadButton";
import SearchIcon from "@mui/icons-material/Search";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import DownloadIcon from "@mui/icons-material/Download";
import { useSubscriptionCapabilities } from "../../hooks/useSubscriptionCapabilities";
import { Typography } from "@mui/material";
import {
  TABLE_ADD_BUTTON_SX,
  TABLE_ICON_BUTTON_SX,
  TABLE_ICON_SX,
  TABLE_SEARCH_ROW_SX,
  PAGE_TABS_BAR_SX,
  PAGE_TAB_LIST_PROPS,
  TAB_PANEL_SX,
} from "../../utils/pageLayoutStyles";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { isPathModuleStaffRole } from "../../utils/pathRoleUtils";
import { addOrgToUrl, getOrganizationId } from "../../utils/organizationUtils";
import { buildPatientTimelineNavigation } from "../../utils/patientTimelineVisitUtils";
import {
  DEFAULT_INVOICE_FETCH_DAYS,
  getDefaultInvoiceFetchRange,
  getInvoiceCacheKey,
  readInvoiceListCache,
  writeInvoiceListCache,
  normalizeApiArray,
  normalizeGroupedAppointments,
  groupAppointmentsByPatientId,
  processInvoiceRows,
  processInvoiceRowsLite,
  computeInvoicePeriodSummary,
  buildInvoiceAmountTileSummaries,
  getFinancialYearBounds,
  normalizeInvoiceRecord,
  buildIdLookupMap,
  fetchPeriodInvoicesForSummary,
  getInvoiceAttachment,
  mergeInvoicesPreservingAttachments,
  readInvoiceAttachmentMemory,
  rememberInvoiceAttachment,
  isExpenseInvoice,
  isIncomeInvoice,
  buildMedicineDepartmentByInvoiceId,
} from "../../utils/invoiceTableUtils";
import { getAllMedicineTransactions } from "../../services/medicineTransactionService";
import { getMedicineInventoryList } from "../../services/medicineInventoryService";
import {
  formatPeriodLabel,
  parsePeriodBounds,
  toPeriodDateKey,
  buildAlignedOpdPeriodExport,
  buildOpdPeriodSummaryForPeriod,
  buildInvoicePatientPeriodSummary,
  resolveAlignedPatientPeriodCounts,
  resolveAssignedDoctorNameFromAppointments,
  getPatientFromDetailsMap,
  getRegistrationDateKey,
  getAppointmentDate,
  buildDepartmentsLookup,
  buildDoctorsLookup,
  isSingleDayPeriod,
} from "../../utils/patientPeriodSummary";
import { downloadInvoiceTableExcel } from "../../utils/patientTableExcelExport";
import { getOrganizationName } from "../../utils/organizationUtils";
import InvoiceSummaryTiles from "../../components/InvoiceSummaryTiles";
import { printInvoiceFromTable } from "../../utils/invoiceReprintUtils";

const INVOICE_TAB = {
  INCOME: "income",
  EXPENSES: "expenses",
};

const INCOME_TYPE_FILTERS = [
  "REGISTRATION",
  "CONSULTATION",
  "PATHOLOGYTEST",
  "MEDICINE",
  "DONATION",
  "MISC",
  "DISCHARGE",
];

const EXPENSE_TYPE_FILTERS = ["Homeopathy", "Allopathy", "Pathology"];

const applyInvoiceUiFilters = (
  invoices,
  {
    searchQuery,
    filterName,
    filterType,
    filterDoctor,
    filterDepartment,
    fromDate,
    toDate,
    applyDateFilter = true,
    invoiceCategory = INVOICE_TAB.INCOME,
  } = {}
) => {
  let filtered = Array.isArray(invoices) ? [...invoices] : [];

  if (invoiceCategory === INVOICE_TAB.EXPENSES) {
    filtered = filtered.filter(isExpenseInvoice);
  } else {
    filtered = filtered.filter(isIncomeInvoice);
  }

  if (searchQuery?.trim()) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter((inv) => {
      const values = [
        inv.id,
        getInvoiceAppointmentId(inv),
        getInvoicePrescriptionId(inv),
        inv.invoiceType,
        inv.patientName,
        inv.doctorName,
        inv.department,
        inv.date,
        inv.createdAt,
        inv.fromName,
        inv.toName,
        inv.totalAmount,
        inv.particulars,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());
      return values.some((v) => v.includes(q));
    });
  }

  if (filterName?.trim()) {
    const n = filterName.toLowerCase();
    filtered = filtered.filter((inv) => {
      const searchableFields = [
        inv.id,
        getInvoiceAppointmentId(inv),
        getInvoicePrescriptionId(inv),
        inv.patientName,
        inv.doctorName,
        inv.department,
        inv.invoiceType,
        inv.fromName,
        inv.toName,
        inv.totalAmount,
        inv.particulars,
      ]
        .filter(Boolean)
        .map((v) => String(v).toLowerCase());

      if (inv.date) {
        try {
          const formattedDate = dayjs(inv.date).isValid()
            ? dayjs(inv.date).format("DD-MM-YYYY").toLowerCase()
            : String(inv.date).toLowerCase();
          searchableFields.push(formattedDate);
        } catch (_) {
          searchableFields.push(String(inv.date).toLowerCase());
        }
      }

      return searchableFields.some((v) => v.includes(n));
    });
  }

  if (filterType) {
    const selectedType = String(filterType).trim().toUpperCase();
    filtered = filtered.filter((inv) => {
      let type = inv.invoiceType;
      if (invoiceCategory !== INVOICE_TAB.EXPENSES && inv.admissionId) {
        type = "DISCHARGE";
      }
      return String(type || "").trim().toUpperCase() === selectedType;
    });
  }

  if (invoiceCategory !== INVOICE_TAB.EXPENSES && filterDoctor) {
    filtered = filtered.filter((inv) => {
      const invDoctorName = String(inv.doctorName || "").toLowerCase();
      const filterDoctorName = String(filterDoctor || "").toLowerCase();
      return invDoctorName === filterDoctorName || invDoctorName.includes(filterDoctorName);
    });
  }

  if (invoiceCategory !== INVOICE_TAB.EXPENSES && filterDepartment) {
    filtered = filtered.filter((inv) => {
      const invDeptName = String(inv.department || "").toLowerCase();
      const filterDeptName = String(filterDepartment || "").toLowerCase();
      return invDeptName === filterDeptName || invDeptName.includes(filterDeptName);
    });
  }

  if (applyDateFilter && (fromDate || toDate)) {
    const startKey = fromDate || null;
    const endKey = toDate || fromDate || null;
    filtered = filtered.filter((inv) => {
      const dateKey = toPeriodDateKey(inv.date ?? inv.createdAt ?? inv.created_at);
      if (!dateKey) return false;
      if (startKey && dateKey < startKey) return false;
      if (endKey && dateKey > endKey) return false;
      return true;
    });
  }

  return filtered.sort((a, b) => (b.id ?? 0) - (a.id ?? 0));
};

dayjs.extend(utc);

const clickableIdSx = {
  color: "#568AF7",
  cursor: "pointer",
  textDecoration: "none",
  fontWeight: 500,
};

const getInvoiceAppointmentId = (invoice) =>
  invoice?.appointmentId ??
  invoice?.appointment_id ??
  invoice?.appointment?.id ??
  null;

const getInvoicePrescriptionId = (invoice) =>
  invoice?.prescriptionId ??
  invoice?.prescription_id ??
  invoice?.prescription?.id ??
  null;

const getInvoiceLinkedPatientId = (invoice) =>
  invoice?.patientId ??
  invoice?.patient_id ??
  invoice?.patient?.id ??
  null;

const isInvoiceLinkedIpd = (invoice) => {
  if (invoice?.admissionId != null && invoice.admissionId !== "") return true;
  if (invoice?.admission_id != null && invoice.admission_id !== "") return true;
  return String(invoice?.patientType || invoice?.patient_type || "")
    .trim()
    .toLowerCase() === "ipd";
};

const incomeColumns = [
  { id: "id", label: "Invoice Id" },
  { id: "appointmentId", label: "Appointment ID" },
  { id: "prescriptionId", label: "Prescription ID" },
  { id: "invoiceType", label: "Type" },
  { id: "patientName", label: "Patient / From Name" },
  { id: "doctorName", label: "Doctor Name" },
  { id: "department", label: "Department" },
  { id: "date", label: "Date" },
  { id: "createdAt", label: "Generated Time" },
  { id: "totalAmount", label: "Amount" },
  { id: "particulars", label: "Comments" },
  { id: "attachment", label: "Attachment" },
];

const expenseColumns = [
  { id: "id", label: "Invoice Id" },
  { id: "invoiceType", label: "Type" },
  { id: "date", label: "Date" },
  { id: "createdAt", label: "Generated Time" },
  { id: "fromName", label: "Supplier Name" },
  { id: "totalAmount", label: "Amount" },
  { id: "particulars", label: "Comments" },
  { id: "attachment", label: "Attachment" },
];

const getIncomePartyDisplayName = (invoice) => {
  if (!invoice || typeof invoice !== "object") return "N/A";

  const patientRaw = String(invoice.patientName || "").trim();
  const patientNormalized = patientRaw.toLowerCase();
  const hasPatient =
    Boolean(patientRaw) &&
    patientNormalized !== "n/a" &&
    patientNormalized !== "unknown patient" &&
    patientNormalized !== "unknown";

  if (hasPatient) return patientRaw;

  const fromRaw = String(invoice.fromName || "").trim();
  if (fromRaw && fromRaw.toLowerCase() !== "n/a") return fromRaw;

  return "N/A";
};

const getInvoiceComments = (invoice) => {
  if (!invoice || typeof invoice !== "object") return "";
  const value =
    invoice.particulars ?? invoice.comments ?? invoice.remarks ?? "";
  return typeof value === "string" ? value.trim() : String(value || "").trim();
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}));

const parseDateTimeToLocalIsoString = (value) => {
  if (!value) return null;

  const finalize = (dateObj) =>
    dateObj && dateObj.isValid() ? dateObj.local().format() : null;

  const normalizeFractional = (input) =>
    input.replace(/\.(\d{3})\d+/, ".$1");

  const buildIsoFromArray = (arr) => {
    const [year, month = 1, day = 1, hour = 0, minute = 0, second = 0] = arr;
    const pad = (num) => String(num).padStart(2, "0");
    return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(minute)}:${pad(second)}`;
  };

  const tryParseString = (rawValue) => {
    if (!rawValue) return null;
    const trimmed = String(rawValue).trim();
    if (!trimmed) return null;

    const base = normalizeFractional(trimmed.replace(" ", "T"));
    const hasOffset = /([+-]\d{2}:?\d{2}|Z)$/i.test(base);
    const utcCandidate = hasOffset ? base : `${base}Z`;

    const parsedUtc = dayjs.utc(utcCandidate);
    if (parsedUtc.isValid()) {
      return finalize(parsedUtc);
    }

    const parsedLocal = dayjs(base);
    if (parsedLocal.isValid()) {
      return parsedLocal.format();
    }

    return null;
  };

  if (Array.isArray(value)) {
    return tryParseString(`${buildIsoFromArray(value)}Z`);
  }

  if (value instanceof Date) {
    return finalize(dayjs(value));
  }

  if (typeof value === "string" || typeof value === "number") {
    return tryParseString(value);
  }

  return null;
};


export default function InvoiceTable() {
  const theme = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const deepLinkInvoiceId = String(searchParams.get("invoiceId") || "").trim();
  const { hasCapability, loading: capabilitiesLoading } = useSubscriptionCapabilities();
  const hasInvoicesCapability = hasCapability("Invoices");
  const isPharmacyAdmin =
    String(user?.roleName || "").toLowerCase() === "pharmacy admin";
  const isPathologyAdmin = isPathModuleStaffRole(user?.roleName);
  const canAccessInvoices =
    hasInvoicesCapability || isPharmacyAdmin || isPathologyAdmin;
  const fetchInProgressRef = useRef(false);
  const [open_invoice_modal, setOpenInvoiceModal] = useState(false);
  const [invoice_info, setInvoiceInfo] = useState({});
  const [printingInvoiceId, setPrintingInvoiceId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fetching, setFetching] = useState({ invoice_list: true, enriching: false });
  const [invoice_list, setInvoiceList] = useState([]);
  const [rawInvoices, setRawInvoices] = useState([]);
  const [enrichmentBundle, setEnrichmentBundle] = useState({
    doctors: [],
    departments: [],
    patients: [],
    admissions: [],
    groupedAppointments: {},
    assignmentsByAdmissionId: {},
    medicineDepartmentByInvoiceId: {},
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [filterName, setFilterName] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterDoctor, setFilterDoctor] = useState("");
  const [filterDepartment, setFilterDepartment] = useState("");
  const [fromDate, setFromDate] = useState(() => getDefaultInvoiceFetchRange().fromDate);
  const [toDate, setToDate] = useState(() => getDefaultInvoiceFetchRange().toDate);
  const [showFilters, setShowFilters] = useState(false);
  const [doctorsList, setDoctorsList] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);
  const [loadedFullHistory, setLoadedFullHistory] = useState(false);
  const [activeTab, setActiveTab] = useState(INVOICE_TAB.INCOME);
  const lastCacheKeyRef = useRef(null);
  const openedDeepLinkRef = useRef(null);
  const [amountTileSummaries, setAmountTileSummaries] = useState(null);
  const [fetchingTileSummary, setFetchingTileSummary] = useState(false);

  const isExpensesTab = activeTab === INVOICE_TAB.EXPENSES;
  const tableColumns = isExpensesTab ? expenseColumns : incomeColumns;
  const typeFilterOptions = isExpensesTab ? EXPENSE_TYPE_FILTERS : INCOME_TYPE_FILTERS;

  const resolvedInvoices = useMemo(() => {
    if (!rawInvoices.length) return [];

    const organizationName = getOrganizationName(user);
    const attachmentMemory = readInvoiceAttachmentMemory(getOrganizationId(user));
    const applyAttachmentMemory = (rows) =>
      (Array.isArray(rows) ? rows : []).map((row) => {
        if (!row || row.id == null) return row;
        const remembered = attachmentMemory[String(row.id)] || "";
        const attachment = getInvoiceAttachment(row) || remembered || "";
        return attachment ? { ...row, attachment } : row;
      });

    const hasLookupData =
      enrichmentBundle.doctors.length > 0 ||
      enrichmentBundle.patients.length > 0 ||
      Object.keys(enrichmentBundle.groupedAppointments || {}).length > 0;

    if (!hasLookupData) {
      return applyAttachmentMemory(
        processInvoiceRowsLite(
          rawInvoices,
          parseDateTimeToLocalIsoString,
          organizationName,
          enrichmentBundle.medicineDepartmentByInvoiceId
        )
      );
    }

    try {
      return applyAttachmentMemory(
        processInvoiceRows(
          rawInvoices,
          {
            doctorsData: enrichmentBundle.doctors,
            departmentsData: enrichmentBundle.departments,
            patientsData: enrichmentBundle.patients,
            admissionsData: enrichmentBundle.admissions,
            groupedAppointments: enrichmentBundle.groupedAppointments,
            assignmentsByAdmissionId: enrichmentBundle.assignmentsByAdmissionId,
            organizationName,
            medicineDepartmentByInvoiceId:
              enrichmentBundle.medicineDepartmentByInvoiceId,
          },
          parseDateTimeToLocalIsoString
        )
      );
    } catch (enrichmentError) {
      console.error("[InvoiceTable] Invoice enrichment failed:", enrichmentError);
      return applyAttachmentMemory(
        processInvoiceRowsLite(
          rawInvoices,
          parseDateTimeToLocalIsoString,
          organizationName,
          enrichmentBundle.medicineDepartmentByInvoiceId
        )
      );
    }
  }, [rawInvoices, enrichmentBundle, user]);

  const fetchAmountTileSummaries = useCallback(async () => {
    setFetchingTileSummary(true);
    try {
      const { fromDate: fyFrom, toDate: fyTo } = getFinancialYearBounds();
      let invoices = await fetchPeriodInvoicesForSummary(fyFrom, fyTo);
      invoices = normalizeApiArray(invoices).map(normalizeInvoiceRecord);
      setAmountTileSummaries(buildInvoiceAmountTileSummaries(invoices));
    } catch (error) {
      console.warn("[InvoiceTable] Failed to load invoice amount summaries:", error);
      setAmountTileSummaries(buildInvoiceAmountTileSummaries([]));
    } finally {
      setFetchingTileSummary(false);
    }
  }, []);

  const fetchAmountTileSummariesRef = useRef(fetchAmountTileSummaries);
  fetchAmountTileSummariesRef.current = fetchAmountTileSummaries;

  const fetchGenerationRef = useRef(0);
  const fetchInvoice = useCallback(async ({ forceRefresh = false, useFullRange = false } = {}) => {
    // Allow forceRefresh to run even if a fetch is in progress (avoids stale list after edit).
    if (fetchInProgressRef.current && !forceRefresh) {
      return;
    }
    const fetchGeneration = ++fetchGenerationRef.current;
    fetchInProgressRef.current = true;

    const orgId = getOrganizationId(user);
    const { fromDate: defaultFromDate, toDate: defaultToDate } = getDefaultInvoiceFetchRange();
    const apiFrom = useFullRange ? null : fromDate || defaultFromDate;
    const apiTo = useFullRange ? null : toDate || defaultToDate;
    const cacheKey = getInvoiceCacheKey(orgId, apiFrom, apiTo, useFullRange);

    if (!forceRefresh) {
      const cached = readInvoiceListCache(cacheKey);
      if (cached?.length) {
        const attachmentMemory = readInvoiceAttachmentMemory(orgId);
        const cachedWithAttachments = mergeInvoicesPreservingAttachments(
          cached,
          [],
          attachmentMemory
        );
        // Seed rawInvoices so later API merges can keep remembered attachments
        setRawInvoices(cachedWithAttachments);
        setInvoiceList(
          [...cachedWithAttachments].sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
        );
        setFetching({ invoice_list: false, enriching: false });
      } else {
        setFetching({ invoice_list: true, enriching: false });
      }
    } else {
      setFetching({ invoice_list: true, enriching: false });
    }

    try {
      const invoiceData = await getInvoiceList(
        useFullRange
          ? { fullRange: true }
          : {
              fromDate: apiFrom || undefined,
              toDate: apiTo || undefined,
            }
      );
      if (fetchGeneration !== fetchGenerationRef.current) return;

      const invoices = normalizeApiArray(invoiceData);
      const orgIdForMemory = getOrganizationId(user);
      const attachmentMemory = readInvoiceAttachmentMemory(orgIdForMemory);
      setRawInvoices((prev) =>
        mergeInvoicesPreservingAttachments(invoices, prev, attachmentMemory)
      );
      lastCacheKeyRef.current = cacheKey;
      setFetching({ invoice_list: false, enriching: true });

      const [doctorsData, departmentsData, patientsData, admissionsData, appointmentsData, medicineTxData, inventoryData] =
        await Promise.all([
          getAllDoctors({ forceRefresh: false }).catch(() => []),
          getAllDepartment().catch(() => []),
          getAllPatients().catch(() => []),
          getAllAdmissions().catch(() => []),
          getAppointmentsGroupedByPatient().catch(() => ({})),
          getAllMedicineTransactions().catch(() => []),
          getMedicineInventoryList().catch(() => []),
        ]);

      if (fetchGeneration !== fetchGenerationRef.current) return;

      let groupedAppointments = normalizeGroupedAppointments(appointmentsData);
      if (Object.keys(groupedAppointments).length === 0) {
        const flatAppointments = await getAllAppointments().catch(() => []);
        groupedAppointments = groupAppointmentsByPatientId(flatAppointments);
      }

      const admissionIdsForAssignments = [
        ...new Set(
          invoices
            .map((invoice) => invoice.admissionId || invoice.admission_id)
            .filter(Boolean)
        ),
      ];

      let assignmentsByAdmissionId = {};
      if (admissionIdsForAssignments.length > 0) {
        try {
          assignmentsByAdmissionId = await fetchAssignmentsByAdmissionIds(
            admissionIdsForAssignments
          );
        } catch (assignmentError) {
          console.warn("[InvoiceTable] Assignment enrichment skipped:", assignmentError);
        }
      }

      if (fetchGeneration !== fetchGenerationRef.current) return;

      const medicineDepartmentByInvoiceId = buildMedicineDepartmentByInvoiceId(
        normalizeApiArray(medicineTxData),
        normalizeApiArray(inventoryData)
      );

      setDoctorsList(normalizeApiArray(doctorsData));
      setDepartmentsList(normalizeApiArray(departmentsData));
      setEnrichmentBundle({
        doctors: normalizeApiArray(doctorsData),
        departments: normalizeApiArray(departmentsData),
        patients: normalizeApiArray(patientsData),
        admissions: normalizeApiArray(admissionsData),
        groupedAppointments,
        assignmentsByAdmissionId,
        medicineDepartmentByInvoiceId,
      });
      setLoadedFullHistory(useFullRange);
    } catch (error) {
      console.error("[InvoiceTable] Failed to fetch invoices:", error);
    } finally {
      if (fetchGeneration === fetchGenerationRef.current) {
        setFetching({ invoice_list: false, enriching: false });
        fetchInProgressRef.current = false;
      }
    }
  }, [fromDate, toDate, user]);

  const fetchInvoiceRef = useRef(fetchInvoice);
  fetchInvoiceRef.current = fetchInvoice;

  useEffect(() => {
    fetchInvoiceRef.current();
    fetchAmountTileSummariesRef.current();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  useEffect(() => {
    if (!resolvedInvoices.length || !lastCacheKeyRef.current) return;
    writeInvoiceListCache(lastCacheKeyRef.current, resolvedInvoices);
  }, [resolvedInvoices]);

  useEffect(() => {
    if (!resolvedInvoices.length) {
      setInvoiceList([]);
      return;
    }
    const finalSorted = applyInvoiceUiFilters(resolvedInvoices, {
      searchQuery,
      filterName,
      filterType,
      filterDoctor,
      filterDepartment,
      fromDate,
      toDate,
      applyDateFilter: Boolean(fromDate || toDate),
      invoiceCategory: activeTab,
    });
    setInvoiceList(finalSorted);
    setPage(0);
  }, [searchQuery, filterName, filterType, filterDoctor, filterDepartment, fromDate, toDate, resolvedInvoices, activeTab]);

  useEffect(() => {
    if (!deepLinkInvoiceId) return;
    setSearchQuery(deepLinkInvoiceId);
    setFromDate("");
    setToDate("");
    setLoadedFullHistory(true);
    fetchInvoice({ forceRefresh: false, useFullRange: true });
  }, [deepLinkInvoiceId, fetchInvoice]);

  useEffect(() => {
    if (!deepLinkInvoiceId || fetching.invoice_list) return;
    if (openedDeepLinkRef.current === deepLinkInvoiceId) return;

    const match = (resolvedInvoices || []).find(
      (inv) =>
        String(inv?.id) === String(deepLinkInvoiceId) ||
        String(inv?.invoiceNumber || inv?.invoice_number || "") ===
          String(deepLinkInvoiceId)
    );

    const focusLinkedInvoice = (invoice) => {
      if (!invoice) return;
      openedDeepLinkRef.current = deepLinkInvoiceId;
      setActiveTab(
        isExpenseInvoice(invoice) ? INVOICE_TAB.EXPENSES : INVOICE_TAB.INCOME
      );
    };

    if (match) {
      focusLinkedInvoice(match);
      return;
    }

    let cancelled = false;
    getInvoiceById(deepLinkInvoiceId)
      .then((fetched) => {
        if (cancelled) return;
        const record = Array.isArray(fetched) ? fetched[0] : fetched;
        if (record) focusLinkedInvoice(record);
      })
      .catch((error) => {
        console.warn("[InvoiceTable] Could not locate linked invoice:", error);
      });

    return () => {
      cancelled = true;
    };
  }, [deepLinkInvoiceId, resolvedInvoices, fetching.invoice_list]);

  const periodLabel = formatPeriodLabel(fromDate, toDate);

  const patientsLookup = useMemo(
    () => buildIdLookupMap(enrichmentBundle?.patients || []),
    [enrichmentBundle?.patients]
  );

  // Show loading state while checking capabilities
  if (capabilitiesLoading) {
    return (
      <Grid container>
        <Grid item xs={12} display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <Typography>Loading...</Typography>
        </Grid>
      </Grid>
    );
  }

  // Hide page if capability not available (pharmacy admin always allowed)
  if (!canAccessInvoices) {
    return (
      <Grid container>
        <Grid item xs={12} display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <Typography variant="h6" color="error">
            Invoices are not available in your subscription plan.
          </Typography>
        </Grid>
      </Grid>
    );
  }

  const clearFilters = () => {
    setSearchQuery("");
    setFilterName("");
    setFilterType("");
    setFilterDoctor("");
    setFilterDepartment("");
    const defaults = getDefaultInvoiceFetchRange();
    setFromDate(defaults.fromDate);
    setToDate(defaults.toDate);
    setLoadedFullHistory(false);
    fetchInvoice({ forceRefresh: true });
    fetchAmountTileSummaries();
  };

  const handleTabChange = (_event, nextTab) => {
    setActiveTab(nextTab);
    setFilterType("");
    if (nextTab === INVOICE_TAB.EXPENSES) {
      setFilterDoctor("");
      setFilterDepartment("");
    }
    setPage(0);
  };

  const handleExport = async () => {
    const { start, end } = parsePeriodBounds(fromDate, toDate);
    const patientDetailsMap = buildIdLookupMap(enrichmentBundle.patients);
    const doctorsById = buildDoctorsLookup(enrichmentBundle.doctors);
    const departmentsById = buildDepartmentsLookup(enrichmentBundle.departments);
    let groupedAppointments = enrichmentBundle.groupedAppointments || {};

    // Income export: refresh appointments so patient counts match Outpatient summary.
    // Expenses have no patients — skip this work.
    if (!isExpensesTab) {
      try {
        const appointmentsData = await getAppointmentsGroupedByPatient();
        let refreshed = normalizeGroupedAppointments(appointmentsData);
        if (Object.keys(refreshed).length === 0) {
          const flatAppointments = await getAllAppointments().catch(() => []);
          refreshed = groupAppointmentsByPatientId(flatAppointments);
        }
        if (Object.keys(refreshed).length > 0) {
          groupedAppointments = refreshed;
        }
      } catch (appointmentsError) {
        console.warn(
          "[InvoiceTable] Could not refresh appointments for export summary:",
          appointmentsError
        );
      }
    }

    // Same period invoice source as PatientsTable — avoids missing REGISTRATION/CONSULTATION
    // rows that are not present in the currently loaded UI list.
    let periodInvoices = [];
    try {
      periodInvoices = await fetchPeriodInvoicesForSummary(fromDate, toDate);
    } catch (error) {
      console.warn("[InvoiceTable] Could not load invoices for export:", error);
      periodInvoices = rawInvoices || [];
    }
    if (!periodInvoices.length) {
      periodInvoices = rawInvoices || [];
    }

    const organizationName = getOrganizationName(user);
    let enrichedPeriodRows = [];
    try {
      enrichedPeriodRows = processInvoiceRows(
        periodInvoices,
        {
          doctorsData: enrichmentBundle.doctors,
          departmentsData: enrichmentBundle.departments,
          patientsData: enrichmentBundle.patients,
          admissionsData: enrichmentBundle.admissions,
          groupedAppointments,
          assignmentsByAdmissionId: enrichmentBundle.assignmentsByAdmissionId,
          organizationName,
          medicineDepartmentByInvoiceId:
            enrichmentBundle.medicineDepartmentByInvoiceId,
        },
        parseDateTimeToLocalIsoString
      );
    } catch (enrichmentError) {
      console.warn("[InvoiceTable] Export enrichment failed, using lite rows:", enrichmentError);
      enrichedPeriodRows = processInvoiceRowsLite(
        periodInvoices,
        parseDateTimeToLocalIsoString,
        organizationName,
        enrichmentBundle.medicineDepartmentByInvoiceId
      );
    }

    // Keep REGISTRATION/CONSULTATION in the sheet unless Type filter explicitly excludes them.
    // Date range already applied by period fetch; re-apply with toPeriodDateKey for consistency.
    const rows = applyInvoiceUiFilters(enrichedPeriodRows, {
      searchQuery,
      filterName,
      filterType,
      filterDoctor,
      filterDepartment,
      fromDate,
      toDate,
      applyDateFilter: Boolean(fromDate || toDate),
      invoiceCategory: activeTab,
    });

    let resolvedPatientCounts = null;
    let patientSheet = null;
    if (!isExpensesTab) {
      const isDaily = isSingleDayPeriod(fromDate || null, toDate || null);

      if (isDaily) {
        // Production daily: appointment summary + invoice-type fill for total only.
        const patientSummary = buildOpdPeriodSummaryForPeriod({
          groupedAppointments,
          patientDetailsMap,
          doctorsById,
          departmentsById,
          invoices: periodInvoices,
          periodStart: start,
          periodEnd: end,
          periodStartKey: fromDate || null,
          periodEndKey: toDate || null,
        });

        const invoiceTypePatientSummary = buildInvoicePatientPeriodSummary({
          invoices: periodInvoices,
          patientDetailsMap,
          periodStart: start,
          periodEnd: end,
          periodStartKey: fromDate || null,
          periodEndKey: toDate || null,
        });

        resolvedPatientCounts = resolveAlignedPatientPeriodCounts(
          patientSummary,
          invoiceTypePatientSummary
        );
      } else {
        // Monthly: same patient-day list as PatientsTable.
        const { rows: exportPatientRows, summary: patientSummary } =
          buildAlignedOpdPeriodExport({
            groupedAppointments,
            invoices: periodInvoices,
            patientDetailsMap,
            doctorsById,
            departmentsById,
            periodStart: start,
            periodEnd: end,
            periodStartKey: fromDate || null,
            periodEndKey: toDate || null,
          });

        resolvedPatientCounts = {
          total: patientSummary.total,
          new: patientSummary.new,
          returning: patientSummary.returning,
        };

        const patientHeaders = [
          "Patient ID",
          "Patient Name",
          "Visit Date",
          "Email",
          "Mobile",
          "Gender",
          "Age",
          "Creation Date",
          "Assigned Doctor",
        ];

        const patientTableRows = exportPatientRows
          .map((r) => {
            const p = getPatientFromDetailsMap(patientDetailsMap, r.patientId) || {};
            const age = p.dob
              ? Math.max(
                  0,
                  Math.floor(
                    (Date.now() - new Date(p.dob).getTime()) /
                      (365.25 * 24 * 60 * 60 * 1000)
                  )
                )
              : "";
            const visitDate =
              r.visitDateKey || toPeriodDateKey(getAppointmentDate(r)) || "";
            const doctor =
              resolveAssignedDoctorNameFromAppointments({
                patientId: r.patientId ?? r.patient_id,
                visitDateKey: visitDate || null,
                groupedAppointments,
                doctorsById,
                fallbackRow: r,
              }) ||
              r.doctorName ||
              (r.doctorId != null && doctorsById[String(r.doctorId)]
                ? doctorsById[String(r.doctorId)].name
                : "") ||
              "";
            const patientName =
              String(r.patientName || p.name || "").trim() ||
              (r.patientId != null ? `Patient ${r.patientId}` : "");

            if (!patientName && (r.patientId == null || r.patientId === "") && !visitDate) {
              return null;
            }

            return [
              r.patientId ?? "",
              patientName,
              visitDate,
              p.email || "",
              p.mobile || "",
              p.gender || "",
              age || "",
              getRegistrationDateKey(p) || "",
              doctor,
            ];
          })
          .filter(Boolean);

        patientSheet = {
          sheetName: "OPD Patients",
          summaryTitle: "Outpatient Summary",
          periodLabel,
          summary: patientSummary,
          tableHeaders: patientHeaders,
          tableRows: patientTableRows,
        };
      }
    }

    const headers = isExpensesTab
      ? [
          "Invoice Id",
          "Type",
          "Date",
          "Generated Time",
          "Supplier Name",
          "Amount",
          "Comments",
          "Attachment",
        ]
      : [
          "Invoice Id",
          "Appointment ID",
          "Prescription ID",
          "Type",
          "Patient / From Name",
          "Doctor Name",
          "Department",
          "Date",
          "Generated Time",
          "Amount",
          "Comments",
          "Attachment",
        ];

    const tableRows = rows.map((r) => {
      let displayType = r.invoiceType;
      if (!isExpensesTab && r.admissionId) {
        displayType = "DISCHARGE";
      }

      if (isExpensesTab) {
        return [
          r.id,
          displayType || "N/A",
          r.date ? dayjs(r.date).format("D MMM YYYY") : "N/A",
          r.createdAt ? dayjs(r.createdAt).format("hh:mm A") : "N/A",
          r.fromName || "N/A",
          r.totalAmount ?? "",
          getInvoiceComments(r) || "",
          getInvoiceAttachment(r) ? "Yes" : "N/A",
        ];
      }

      return [
        r.id,
        getInvoiceAppointmentId(r) || "N/A",
        getInvoicePrescriptionId(r) || "N/A",
        displayType || "N/A",
        getIncomePartyDisplayName(r),
        r.doctorName || "N/A",
        r.department || "N/A",
        r.date ? dayjs(r.date).format("D MMM YYYY") : "N/A",
        r.createdAt ? dayjs(r.createdAt).format("hh:mm A") : "N/A",
        r.totalAmount ?? "",
        getInvoiceComments(r) || "",
        getInvoiceAttachment(r) ? "Yes" : "N/A",
      ];
    });

    await downloadInvoiceTableExcel({
      filename: `${isExpensesTab ? "invoice_expenses" : "invoice_income"}_${fromDate || "all"}_${toDate || "all"}`,
      sheetName: isExpensesTab ? "Expenses" : "Income",
      organizationName: getOrganizationName(user),
      summaryTitle: isExpensesTab ? "Expense Invoice Summary" : "Invoice Summary",
      periodLabel,
      fromDate: fromDate || null,
      toDate: toDate || null,
      summaryVariant: isExpensesTab ? "expense" : "income",
      summary: computeInvoicePeriodSummary(rows, resolvedPatientCounts),
      tableHeaders: headers,
      tableRows,
      patientSheet,
    });
  };

  const handlePrintInvoice = async (invoice) => {
    if (!invoice?.id || printingInvoiceId != null) return;
    setPrintingInvoiceId(invoice.id);
    try {
      await printInvoiceFromTable(invoice, {
        user,
        patientsLookup,
      });
    } catch (error) {
      console.error("[InvoiceTable] Failed to print invoice:", error);
      toast.error("Failed to print invoice");
    } finally {
      setPrintingInvoiceId(null);
    }
  };

  const navigateToLinkedPatientPage = (invoice, timelineTab) => {
    const linkedPatientId = getInvoiceLinkedPatientId(invoice);
    if (linkedPatientId == null || linkedPatientId === "") {
      toast.info("Patient is not linked for this invoice");
      return;
    }
    const isIpd = isInvoiceLinkedIpd(invoice);
    const { path, state } = buildPatientTimelineNavigation({
      user,
      patientId: linkedPatientId,
      isIpd,
      timelineTab,
    });
    navigate(addOrgToUrl(path, user), state ? { state } : undefined);
  };

  const handleAppointmentIdClick = (invoice) => {
    const appointmentId = getInvoiceAppointmentId(invoice);
    if (appointmentId == null || appointmentId === "") {
      toast.info("Appointment ID is not available for this invoice");
      return;
    }
    navigateToLinkedPatientPage(invoice, "appointments");
  };

  const handlePrescriptionIdClick = (invoice) => {
    const prescriptionId = getInvoicePrescriptionId(invoice);
    if (prescriptionId == null || prescriptionId === "") {
      toast.info("Prescription ID is not available for this invoice");
      return;
    }
    navigateToLinkedPatientPage(invoice, "prescriptions");
  };

  return (
    <PageShell
      title="INVOICES"
      paperSx={{ mt: { md: -1.5 } }}
      headerRight={
        loadedFullHistory ? (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: { xs: "none", md: "block" } }}
          >
            Showing all invoices
          </Typography>
        ) : (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: { xs: "none", md: "block" } }}
          >
            Showing last {DEFAULT_INVOICE_FETCH_DAYS} days
          </Typography>
        )
      }
    >
        <TabContext value={activeTab}>
          <Box sx={{ ...PAGE_TABS_BAR_SX, mt: 0 }}>
            <TabList
              {...PAGE_TAB_LIST_PROPS}
              onChange={handleTabChange}
              aria-label="invoice income and expenses tabs"
            >
              <Tab label="Income" value={INVOICE_TAB.INCOME} />
              <Tab label="Expenses" value={INVOICE_TAB.EXPENSES} />
            </TabList>
          </Box>

          <Box sx={{ ...TAB_PANEL_SX, pt: 0.25 }}>
      <Paper sx={{ width: "100%" }}>
        <Box sx={{ px: 1.5, pt: 0.5, pb: 0.65 }}>
          <InvoiceSummaryTiles
            summaries={amountTileSummaries}
            loading={fetchingTileSummary}
          />
          <Box sx={{ ...TABLE_SEARCH_ROW_SX, flexWrap: "wrap", rowGap: 0.75 }}>
            <TextField
              placeholder="Search"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
              sx={{ width: { xs: "100%", sm: "91%" }, flexShrink: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={TABLE_ICON_SX} fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title={showFilters ? "Hide filters" : "Show filters"}>
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => setShowFilters((v) => !v)}
              >
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export to Excel">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={handleExport}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip
              title={
                loadedFullHistory
                  ? `Showing all invoices — click to switch to last ${DEFAULT_INVOICE_FETCH_DAYS} days`
                  : `Showing last ${DEFAULT_INVOICE_FETCH_DAYS} days — click to load all invoices`
              }
            >
              <IconButton
                size="small"
                sx={TABLE_ICON_BUTTON_SX}
                onClick={() => {
                  if (loadedFullHistory) {
                    setLoadedFullHistory(false);
                    const defaults = getDefaultInvoiceFetchRange();
                    setFromDate(defaults.fromDate);
                    setToDate(defaults.toDate);
                    fetchInvoice({ forceRefresh: true, useFullRange: false });
                  } else {
                    fetchInvoice({ forceRefresh: true, useFullRange: true });
                  }
                  fetchAmountTileSummaries();
                }}
                disabled={fetching.invoice_list}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
            {!isExpensesTab && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{ ...TABLE_ADD_BUTTON_SX, flexShrink: 0, whiteSpace: "nowrap" }}
                onClick={() => {
                  setInvoiceInfo({});
                  setOpenInvoiceModal(true);
                }}
              >
                Create Invoice
              </Button>
            )}
          </Box>

          {showFilters && (
          <Stack spacing={1} direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Filter by any field"
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
            />
            <TextField
              size="small"
              label="Type"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              select
              sx={{ minWidth: 180 }}
            >
              <MenuItem value="">All</MenuItem>
              {typeFilterOptions.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
            {!isExpensesTab && (
              <TextField
                size="small"
                label="Doctor name"
                value={filterDoctor}
                onChange={(e) => setFilterDoctor(e.target.value)}
                select
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="">All Doctors</MenuItem>
                {doctorsList
                  .filter((doctor) => doctor.name || doctor.doctorName || doctor.fullName)
                  .map((doctor) => {
                    const doctorName = doctor.name || doctor.doctorName || doctor.fullName;
                    return (
                      <MenuItem key={doctor.id} value={doctorName}>
                        {doctorName}
                      </MenuItem>
                    );
                  })}
              </TextField>
            )}
            {!isExpensesTab && (
              <TextField
                size="small"
                label="Department"
                value={filterDepartment}
                onChange={(e) => setFilterDepartment(e.target.value)}
                select
                sx={{ minWidth: 180 }}
              >
                <MenuItem value="">All Departments</MenuItem>
                {departmentsList
                  .filter((dept) => dept.name || dept.departmentName)
                  .map((dept) => {
                    const deptName = dept.name || dept.departmentName;
                    return (
                      <MenuItem key={dept.id} value={deptName}>
                        {deptName}
                      </MenuItem>
                    );
                  })}
              </TextField>
            )}
            <TextField
              size="small"
              label="From date"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              size="small"
              label="To date"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)} 
              InputLabelProps={{ shrink: true }}
            />
            <Tooltip title="Clear filters">
              <IconButton size="small" sx={TABLE_ICON_BUTTON_SX} onClick={clearFilters}>
                <ClearAllIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          )}
        </Box>
        <TableContainer sx={{ maxHeight: "calc(100vh - 460px)" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {tableColumns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    align={column.align}
                    style={{ top: 57, minWidth: column.minWidth }}
                  >
                    {column.label}
                  </StyledTableCell>
                ))}
                <StyledTableCell key={"action"} style={{ top: 57 }}>
                  Action
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {fetching.invoice_list && invoice_list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tableColumns.length + 1} align="center">
                    <CircularProgress size={40} />
                  </TableCell>
                </TableRow>
              ) : invoice_list.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tableColumns.length + 1} align="center">
                    <Alert severity="info">
                      {isExpensesTab ? "No expense invoices" : "No Invoice"}
                    </Alert>
                  </TableCell>
                </TableRow>
              ) : (
                invoice_list
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.id || row.code || `invoice-${row.id || Math.random()}`}
                      >
                        {tableColumns.map((column) => {
                          const value = row[column.id];

                            if (column.id === "invoiceType") {
    let displayType = row.invoiceType;

    if (!isExpensesTab && row.admissionId) {
      displayType = "DISCHARGE";
    }

    return (
      <TableCell key={column.id}>
        {displayType || "N/A"}
      </TableCell>
    );
  }
                          
                          if (column.id === "date") {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {value ? dayjs(value).format("D MMM YYYY") : "N/A"}
                              </TableCell>
                            );
                          }
                          if (column.id === "createdAt") {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {value ? dayjs(value).format("hh:mm A") : "N/A"}
                              </TableCell>
                            );
                          }
                          if (column.id === "appointmentId" || column.id === "prescriptionId") {
                            const linkedId =
                              column.id === "appointmentId"
                                ? getInvoiceAppointmentId(row)
                                : getInvoicePrescriptionId(row);
                            const handleClick =
                              column.id === "appointmentId"
                                ? handleAppointmentIdClick
                                : handlePrescriptionIdClick;
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {linkedId != null && linkedId !== "" ? (
                                  <Box
                                    component="span"
                                    sx={clickableIdSx}
                                    onClick={() => handleClick(row)}
                                    title={
                                      column.id === "appointmentId"
                                        ? "Open appointment"
                                        : "Open prescription"
                                    }
                                  >
                                    {linkedId}
                                  </Box>
                                ) : (
                                  "N/A"
                                )}
                              </TableCell>
                            );
                          }
                          if (column.id === "patientName") {
                            const displayName = getIncomePartyDisplayName(row);
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {displayName}
                              </TableCell>
                            );
                          }
                          if (column.id === "fromName") {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                {value || "N/A"}
                              </TableCell>
                            );
                          }
                          if (column.id === "particulars") {
                            const comments = getInvoiceComments(row);
                            return (
                              <TableCell
                                key={column.id}
                                align={column.align}
                                sx={{
                                  maxWidth: 220,
                                  whiteSpace: "normal",
                                  wordBreak: "break-word",
                                }}
                                title={comments || undefined}
                              >
                                {comments || "N/A"}
                              </TableCell>
                            );
                          }
                          if (column.id === "attachment") {
                            return (
                              <TableCell key={column.id} align={column.align}>
                                <InvoiceAttachmentDownloadButton invoice={row} silent />
                              </TableCell>
                            );
                          }

                          return (
                            <TableCell key={column.id} align={column.align}>
                              {column.format && typeof value === "number"
                                ? column.format(value)
                                : value || "N/A"}
                            </TableCell>
                          );
                        })}
                        <TableCell key={`action-${row.id || row.code || Math.random()}`}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                            <Tooltip title="Print invoice">
                              <span>
                                <IconButton
                                  size="small"
                                  sx={TABLE_ICON_BUTTON_SX}
                                  disabled={printingInvoiceId != null}
                                  onClick={() => handlePrintInvoice(row)}
                                >
                                  {printingInvoiceId != null &&
                                  String(printingInvoiceId) === String(row.id) ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <PrintIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Edit invoice">
                              <IconButton
                                size="small"
                                sx={TABLE_ICON_BUTTON_SX}
                                onClick={() => {
                                  setInvoiceInfo(row);
                                  setOpenInvoiceModal(true);
                                }}
                              >
                                <ModeEditOutlineTwoToneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 100]}
          component="div"
          count={invoice_list.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
          </Box>
        </TabContext>
      <AddNewInvoice
        key={
          open_invoice_modal
            ? `invoice-modal-${invoice_info?.id || "create"}`
            : "invoice-modal-closed"
        }
        open={open_invoice_modal}
        onClose={async (updatedInvoice) => {
          setOpenInvoiceModal(false);
          setInvoiceInfo({});
          // Keep attachment/comments visible even if list API omits them after refresh
          if (updatedInvoice?.id) {
            const orgId = getOrganizationId(user);
            const attachmentUrl = getInvoiceAttachment(updatedInvoice);
            const hasAttachmentField = Object.prototype.hasOwnProperty.call(
              updatedInvoice,
              "attachment"
            );
            // Only clear remembered attachment when the saved invoice explicitly cleared it
            if (attachmentUrl) {
              rememberInvoiceAttachment(orgId, updatedInvoice.id, attachmentUrl);
            } else if (hasAttachmentField) {
              rememberInvoiceAttachment(orgId, updatedInvoice.id, "");
            }

            const patchInvoice = (inv) =>
              String(inv.id) === String(updatedInvoice.id)
                ? {
                    ...inv,
                    ...updatedInvoice,
                    attachment:
                      attachmentUrl ||
                      (!hasAttachmentField
                        ? getInvoiceAttachment(inv) || inv.attachment || null
                        : null),
                    particulars:
                      updatedInvoice.particulars ??
                      updatedInvoice.comments ??
                      updatedInvoice.remarks ??
                      inv.particulars ??
                      null,
                  }
                : inv;

            setRawInvoices((prev) => {
              const list = Array.isArray(prev) ? prev : [];
              const idx = list.findIndex(
                (inv) => String(inv.id) === String(updatedInvoice.id)
              );
              if (idx === -1) return list;
              const next = [...list];
              next[idx] = patchInvoice(next[idx]);
              return next;
            });
            setInvoiceList((prev) =>
              (Array.isArray(prev) ? prev : []).map(patchInvoice)
            );
          }
          await fetchInvoice({ forceRefresh: true, useFullRange: loadedFullHistory });
          await fetchAmountTileSummaries();
        }}
        invoice_info={invoice_info}
      />
    </PageShell>
  );
}
